/**
 * useAccountManagement - アカウント管理用のComposable
 * 
 * アカウントのCRUD操作とモーダル管理を提供します。
 */
import { ref, reactive, computed } from 'vue'
import { developerSettingsApi } from '@/services'

export function useAccountManagement() {
    // アカウントデータ
    const accounts = ref([])
    const loading = reactive({
        accounts: false
    })

    // モーダル状態
    const modals = reactive({
        accountCreate: { visible: false },
        accountEdit: { visible: false, account: null },
        accountDelete: { visible: false, account: null },
    })

    // アカウント用のヘッダー定義
    const accountHeaders = computed(() => {
        return ['ユーザーID', '属性', '企業', 'パスワード', '操作']
    })

    // アカウント用のアクションボタン定義
    const accountActionButtons = computed(() => {
        return [
            { label: '編集', variant: 'success', action: 'edit' },
            { label: '削除', variant: 'danger', action: 'delete' }
        ]
    })

    // アカウントデータをInfoTableSection用に変換
    const formattedAccounts = computed(() => {
        return accounts.value.map(account => ({
            id: account.id,
            name: account.userId,
            attribute: account.attribute,
            company: account.company,
            password: account.password || '未設定',
            originalAccount: account
        }))
    })

    // アカウント削除確認メッセージ
    const accountDeleteMessage = computed(() => {
        if (modals.accountDelete.account) {
            const userId = modals.accountDelete.account.userId || modals.accountDelete.account.name
            return `「${userId}」を削除しますがよろしいですか？`
        }
        return 'アカウントを削除しますがよろしいですか？'
    })

    /**
     * アカウント一覧データを読み込み
     */
    const loadAccounts = async () => {
        loading.accounts = true
        try {
            console.log('[Container] アカウント一覧の取得を開始')
            accounts.value = await developerSettingsApi.getAccounts()
            console.log('[Container] アカウント一覧の取得完了:', accounts.value)
            return null // エラーなし
        } catch (error) {
            console.error('[Container] アカウント一覧の取得に失敗しました:', error)
            return 'アカウント一覧の読み込みに失敗しました。'
        } finally {
            loading.accounts = false
        }
    }

    /**
     * 新規追加モーダルを表示
     */
    const handleShowCreateModal = () => {
        modals.accountCreate.visible = true
    }

    /**
     * アカウント編集モーダルを表示
     */
    const handleEditAccount = (account) => {
        modals.accountEdit.account = { ...account }
        modals.accountEdit.visible = true
    }

    /**
     * アカウント削除モーダルを表示
     */
    const handleDeleteAccount = (account) => {
        modals.accountDelete.account = account
        modals.accountDelete.visible = true
    }

    /**
     * InfoTableSectionからのアクションイベントを処理
     */
    const handleAccountAction = ({ action, data }) => {
        const originalAccount = data.originalAccount

        if (action === 'edit') {
            handleEditAccount(originalAccount)
        } else if (action === 'delete') {
            handleDeleteAccount(originalAccount)
        }
    }

    /**
     * アカウント作成保存の処理
     */
    const handleAccountCreateSave = async (accountData) => {
        try {
            console.log('[Container] アカウント作成保存開始:', accountData)
            const newAccount = await developerSettingsApi.createAccount(accountData)
            accounts.value.push(newAccount)
            modals.accountCreate.visible = false
            console.log('[Container] アカウント作成保存完了')
            return null // エラーなし
        } catch (error) {
            console.error('[Container] アカウント作成保存に失敗しました:', error)
            return error.message
        }
    }

    /**
     * アカウント編集保存の処理
     */
    const handleAccountEditSave = async (accountData) => {
        try {
            console.log('[Container] アカウント編集保存開始:', accountData)
            const updatedAccount = await developerSettingsApi.updateAccount(accountData.id, accountData)

            const index = accounts.value.findIndex(acc => acc.id === accountData.id)
            if (index !== -1) {
                accounts.value[index] = updatedAccount
            }

            modals.accountEdit.visible = false
            console.log('[Container] アカウント編集保存完了')
            return null // エラーなし
        } catch (error) {
            console.error('[Container] アカウント編集保存に失敗しました:', error)
            return error.message
        }
    }

    /**
     * アカウント削除確認の処理
     */
    const handleAccountDeleteConfirm = async () => {
        try {
            if (modals.accountDelete.account) {
                const accountId = modals.accountDelete.account.id
                console.log('[Container] アカウント削除開始:', accountId)

                await developerSettingsApi.deleteAccount(accountId)

                const index = accounts.value.findIndex(acc => acc.id === accountId)
                if (index !== -1) {
                    accounts.value.splice(index, 1)
                    modals.accountDelete.visible = false
                }

                console.log('[Container] アカウントを削除しました')
                return null // エラーなし
            }
        } catch (error) {
            console.error('[Container] 削除確認処理に失敗しました:', error)
            return error.message
        }
    }

    return {
        // State
        accounts,
        loading,
        modals,

        // Computed
        accountHeaders,
        accountActionButtons,
        formattedAccounts,
        accountDeleteMessage,

        // Methods
        loadAccounts,
        handleShowCreateModal,
        handleEditAccount,
        handleDeleteAccount,
        handleAccountAction,
        handleAccountCreateSave,
        handleAccountEditSave,
        handleAccountDeleteConfirm,
    }
}
